/**
 * StreakCelebration - Subtle celebration modal for streak milestones
 * Industrial design with orange glow effects
 */

import { useEffect, useState } from 'react';
import { Flame, X, Trophy } from 'lucide-react';
import { useCelebrationStore } from '@stores/celebration.store';

export function StreakCelebration(): JSX.Element | null {
  const { activeCelebration, dismissCelebration } = useCelebrationStore();
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (activeCelebration) {
      // Small delay for entrance animation
      const showTimer = setTimeout(() => setIsVisible(true), 50);

      // Auto-dismiss after 5 seconds
      const dismissTimer = setTimeout(() => {
        handleDismiss();
      }, 5000);

      return () => {
        clearTimeout(showTimer);
        clearTimeout(dismissTimer);
      };
    }
    setIsVisible(false);
    return undefined;
  }, [activeCelebration]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsExiting(false);
      dismissCelebration();
    }, 300);
  };

  if (!activeCelebration) return null;

  const getIcon = () => {
    switch (activeCelebration.type) {
      case 'streak':
        return <Flame className="text-ybh-orange" size={40} />;
      case 'target_complete':
        return <Trophy className="text-ybh-success" size={40} />;
      default:
        return <Flame className="text-ybh-orange" size={40} />;
    }
  };

  const getGlowColor = () => {
    switch (activeCelebration.type) {
      case 'streak':
        return 'shadow-[0_0_60px_rgba(232,123,7,0.4)]';
      case 'target_complete':
        return 'shadow-[0_0_60px_rgba(34,197,94,0.4)]';
      default:
        return 'shadow-[0_0_60px_rgba(232,123,7,0.4)]';
    }
  };

  return (
    <div
      className={`
        fixed inset-0 z-50 flex items-center justify-center
        transition-all duration-300
        ${isVisible && !isExiting ? 'opacity-100' : 'opacity-0'}
      `}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleDismiss}
      />

      {/* Celebration card */}
      <div
        className={`
          relative bg-ybh-bg-secondary border border-white/[0.04] rounded-2xl p-8
          max-w-sm w-full mx-4 text-center
          transition-all duration-300 ease-out
          ${getGlowColor()}
          ${isVisible && !isExiting
            ? 'scale-100 translate-y-0'
            : 'scale-95 translate-y-4'}
        `}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-ybh-bg-elevated transition-colors"
        >
          <X size={16} className="text-ybh-text-muted" />
        </button>

        {/* Icon with glow */}
        <div className="relative inline-flex mb-6">
          <div className="w-20 h-20 rounded-full bg-ybh-gradient flex items-center justify-center animate-pulse-slow">
            {getIcon()}
          </div>
          {/* Radiating rings */}
          <div className="absolute inset-0 rounded-full border-2 border-ybh-orange/30 animate-ping" />
        </div>

        {/* Streak value */}
        {activeCelebration.type === 'streak' && (
          <div className="flex items-center justify-center gap-2 mb-4">
            <Flame size={24} className="text-ybh-orange" />
            <span className="font-display text-5xl text-ybh-orange tracking-wide">
              {activeCelebration.value}
            </span>
            <span className="font-display text-2xl text-ybh-text-muted">DAYS</span>
          </div>
        )}

        {/* Message */}
        <h2 className="font-display text-3xl text-ybh-text-primary tracking-wide mb-2">
          {activeCelebration.message}
        </h2>
        {activeCelebration.subMessage && (
          <p className="text-ybh-text-secondary text-sm">
            {activeCelebration.subMessage}
          </p>
        )}

        {/* Dismiss hint */}
        <p className="text-xs text-ybh-text-muted mt-6">
          Click anywhere to dismiss
        </p>
      </div>
    </div>
  );
}
